import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import { celebrateWinner } from '../../lib/confetti';
import { WinnerBadge } from '../ui/Badge';
import Button from '../ui/Button';

/**
 * WinnerBanner - Lucky Number Design System
 *
 * Celebration component for winners with accent glow,
 * confetti animation, and winner-reveal animation.
 */
export default function WinnerBanner({
  ticketNumber,
  raffleTitle,
  onConfirmCollection,
  onViewDetails,
  primaryButtonLabel = 'Confirm Collection',
  secondaryButtonLabel = 'View Details',
  showConfetti = true,
  compact = false,
  className,
}) {
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (showConfetti && !hasAnimated && !compact) {
      // Trigger confetti celebration (not in compact mode)
      celebrateWinner();
      setHasAnimated(true);
    }
  }, [showConfetti, hasAnimated, compact]);

  // Compact version for manage page
  if (compact) {
    return (
      <div
        className={cn(
          'bg-gradient-to-r from-accent/20 to-accent/10',
          'border border-accent/30 rounded-xl',
          'p-4 flex items-center gap-4',
          className
        )}
      >
        <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center shrink-0">
          <span className="text-2xl">🏆</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-accent font-bold text-sm uppercase tracking-wider">
            Winner
          </p>
          <p className="text-cream/70 text-sm">
            Winning ticket: <span className="font-mono text-accent">#{ticketNumber}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        'bg-gradient-to-br from-midnight-light to-midnight',
        'border-2 border-accent/50 rounded-2xl',
        'p-8 text-center',
        'shadow-glow-accent animate-winner-reveal',
        className
      )}
    >
      {/* Background glow */}
      <div className="absolute inset-0 bg-mesh-accent opacity-30" />

      {/* Content */}
      <div className="relative z-10">
        {/* Trophy icon */}
        <div className="w-20 h-20 mx-auto mb-6 bg-accent/20 rounded-full flex items-center justify-center animate-accent-pulse">
          <span className="text-5xl">🏆</span>
        </div>

        {/* Title */}
        <h2 className="font-display text-3xl md:text-4xl text-cream mb-2">
          You Won!
        </h2>

        {/* Raffle title */}
        <p className="text-accent text-lg mb-4">
          {raffleTitle}
        </p>

        {/* Winner badge */}
        <div className="flex justify-center mb-6">
          <WinnerBadge />
        </div>

        {/* Winning ticket display */}
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-wider text-cream/50 mb-2">
            Your Winning Ticket
          </p>
          <LuckyTicketDisplay number={ticketNumber} />
        </div>

        {/* Actions */}
        {(onConfirmCollection || onViewDetails) && (
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {onConfirmCollection && (
              <Button variant="gold" size="lg" onClick={onConfirmCollection}>
                {primaryButtonLabel}
              </Button>
            )}
            {onViewDetails && (
              <Button variant="outline" onClick={onViewDetails}>
                {secondaryButtonLabel}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * LuckyTicketDisplay
 *
 * Shows the winning ticket number with slot machine animation.
 */
function LuckyTicketDisplay({ number }) {
  const digits = String(number).padStart(4, '0').split('');

  return (
    <div className="inline-flex items-center gap-2">
      <span className="text-accent text-sm font-mono">#</span>
      {digits.map((digit, i) => (
        <div
          key={i}
          className="lucky-number animate-number-slot"
          style={{ animationDelay: `${i * 0.1}s` }}
        >
          {digit}
        </div>
      ))}
    </div>
  );
}

/**
 * WinnerCard
 *
 * Compact winner notification card.
 */
export function WinnerCard({
  raffleTitle,
  raffleImage,
  ticketNumber,
  onView,
  className,
}) {
  return (
    <div
      className={cn(
        'bg-midnight-light rounded-xl p-4',
        'border border-accent/30 shadow-glow-accent',
        'flex items-center gap-4',
        className
      )}
    >
      {/* Image */}
      {raffleImage && (
        <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
          <img
            src={raffleImage}
            alt={raffleTitle}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-accent text-lg">🏆</span>
          <span className="text-sm font-bold uppercase tracking-wider text-accent">
            You Won!
          </span>
        </div>
        <p className="text-cream font-display text-lg truncate">
          {raffleTitle}
        </p>
        <p className="text-cream/50 text-sm">
          Ticket #{ticketNumber}
        </p>
      </div>

      {/* Action */}
      <Button variant="gold" size="sm" onClick={onView}>
        View
      </Button>
    </div>
  );
}

/**
 * WinnerNotification
 *
 * Toast-style winner notification.
 */
export function WinnerNotification({
  raffleTitle,
  ticketNumber,
  onDismiss,
  onView,
  className,
}) {
  useEffect(() => {
    // Auto-dismiss after 10 seconds
    const timer = setTimeout(() => {
      onDismiss?.();
    }, 10000);

    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div
      className={cn(
        'fixed bottom-4 right-4 z-toast max-w-sm',
        'bg-midnight-light rounded-xl p-4',
        'border-2 border-accent/50 shadow-glow-accent',
        'animate-slide-in-right',
        className
      )}
    >
      {/* Close button */}
      <button
        onClick={onDismiss}
        className="absolute top-2 right-2 text-cream/50 hover:text-cream transition-colors"
        aria-label="Dismiss"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center shrink-0">
          <span className="text-2xl">🎉</span>
        </div>

        <div>
          <p className="text-accent font-bold uppercase tracking-wider text-sm mb-1">
            Congratulations!
          </p>
          <p className="text-cream mb-1">
            You won <span className="font-display">{raffleTitle}</span>
          </p>
          <p className="text-cream/50 text-sm mb-3">
            Ticket #{ticketNumber}
          </p>

          <Button variant="gold" size="sm" onClick={onView}>
            View Prize
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * OddsDisplay
 *
 * Shows user's odds of winning.
 */
export function OddsDisplay({
  userTickets,
  totalTickets,
  className,
}) {
  const odds = totalTickets > 0 ? Math.round((userTickets / totalTickets) * 100) : 0;

  return (
    <div className={cn('text-center', className)}>
      <p className="text-xs font-bold uppercase tracking-wider text-cream/50 mb-2">
        Your Odds
      </p>
      <div className="flex items-center justify-center gap-1">
        {String(userTickets).split('').map((digit, i) => (
          <div key={i} className="lucky-number text-base">
            {digit}
          </div>
        ))}
        <span className="text-cream/50 mx-1">/</span>
        <span className="text-cream font-mono">{totalTickets}</span>
      </div>
      <p className="text-accent font-bold mt-1">
        {odds}%
      </p>
    </div>
  );
}
